/**
 * 品牌详情页
 *
 * 展示品牌基本信息 + 配置信息 + 两级密码 + 通用入口二维码 + 订阅状态。
 *
 * 客户入口已简化为「一个二维码 + 两级密码」：
 * - 通用入口二维码：只有一个（?brand=<id>），老板和员工都扫它；
 * - 员工通用口令 access_code → 普通权限（发笔记）；
 * - 老板专属密码 boss_password → 最高权限（发笔记 + 老板看板），每个品牌单独配置。
 */

import { useState, useEffect } from 'react'
import type { ChangeEvent } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  AlertColor,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import TuneIcon from '@mui/icons-material/Tune'
import QrCodeIcon from '@mui/icons-material/QrCode'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import DownloadIcon from '@mui/icons-material/Download'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import StorefrontIcon from '@mui/icons-material/Storefront'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import { AdminLayout } from '@/components/AdminLayout'
import { BrandFormDialog } from '@/components/BrandFormDialog'
import { BrandConfigDialog } from '@/components/BrandConfigDialog'
import {
  getBrandDetail,
  updateBrand,
  updateBrandConfig,
  getBrandQrcode,
  resetAccessCode,
  listStores,
  createStore,
  updateStore,
  deleteStore,
} from '@/api/services'
import type { Brand, BrandConfig, Store } from '@chaoyinli/shared'
import type {
  BrandUpdateParams,
  BrandConfigParams,
  QrcodeResponse,
  QrVariant,
  BrandDetail,
  StoreCreateParams,
  StoreUpdateParams,
} from '@/api/services'
import { formatDate, formatDateOnly } from '@/utils/format'
import { copyToClipboard } from '@/utils/clipboard'

const STATUS_LABELS: Record<string, string> = {
  trial: '试用中',
  active: '已激活',
  expired: '已过期',
}

const STATUS_COLORS: Record<string, string> = {
  trial: '#FF9800',
  active: '#4CAF50',
  expired: '#F44336',
}

/** 品牌通用口令列表脱敏展示：前 4 后 2 中间 * */
function maskAccessCode(code: string): string {
  if (!code || code.length <= 6) return code
  return code.slice(0, 4) + '****' + code.slice(-2)
}

/** H5 主题默认色（与后端 BrandConfig 列默认值保持一致） */
const DEFAULT_PRIMARY_COLOR = '#1976d2'
const DEFAULT_SECONDARY_COLOR = '#dc004e'

/**
 * 校验品牌英文标识（slug）：3-64 位小写字母 / 数字 / 中划线，不以中划线开头或结尾。
 * 规则与后端 tenant_service.validate_brand_slug 一一对应，避免白跑一趟 400。
 */
function isValidSlug(slug: string): boolean {
  if (slug.length < 3 || slug.length > 64) return false
  if (slug.startsWith('-') || slug.endsWith('-')) return false
  return /^[a-z0-9-]+$/.test(slug)
}

export function BrandDetailPage() {
  const { id: brandId } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [brand, setBrand] = useState<BrandDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // 弹窗
  const [formOpen, setFormOpen] = useState(false)
  const [configOpen, setConfigOpen] = useState(false)
  const [qrcodeData, setQrcodeData] = useState<QrcodeResponse | null>(null)
  const [qrcodeOpen, setQrcodeOpen] = useState(false)
  // 二维码落地形态：默认海报版（扫码进 /qr/<key> 品牌海报页，老板可长按存图转发）
  const [qrVariant, setQrVariant] = useState<QrVariant>('poster')
  const [submitting, setSubmitting] = useState(false)
  // 通用口令展示 / 重置
  const [showCode, setShowCode] = useState(false)
  const [resetOpen, setResetOpen] = useState(false)
  const [newCode, setNewCode] = useState('')
  const [resetting, setResetting] = useState(false)
  // 老板专属密码（每个品牌单独配置，明文下发给老板）
  const [bossPassword, setBossPassword] = useState('')
  const [savingBossPassword, setSavingBossPassword] = useState(false)
  // 客户英文标识（slug）：入口参数 ?brand=<slug>，替代 UUID
  const [slug, setSlug] = useState('')
  const [savingSlug, setSavingSlug] = useState(false)
  // H5 主题色（方案 A：每品牌主色 + 辅色）
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY_COLOR)
  const [secondaryColor, setSecondaryColor] = useState(DEFAULT_SECONDARY_COLOR)
  const [savingColors, setSavingColors] = useState(false)

  // ===== 门店管理（P0-1 门店独立化）=====
  const [stores, setStores] = useState<Store[]>([])
  const [storesLoading, setStoresLoading] = useState(false)
  // 门店表单弹窗
  const [storeDialogOpen, setStoreDialogOpen] = useState(false)
  const [editingStore, setEditingStore] = useState<Store | null>(null) // null=新增
  const [storeForm, setStoreForm] = useState({
    name: '',
    city: '',
    address: '',
    signature_dishes: '', // 以换行 / 顿号 / 逗号分隔输入，提交时拆成数组
    sort_order: 0,
    is_active: true,
  })
  const [savingStore, setSavingStore] = useState(false)
  // 门店删除确认弹窗
  const [deleteStoreTarget, setDeleteStoreTarget] = useState<Store | null>(null)
  const [deletingStore, setDeletingStore] = useState(false)

  // Toast
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: AlertColor }>({
    open: false,
    message: '',
    severity: 'info',
  })

  const showToast = (message: string, severity: AlertColor = 'info') => {
    setToast({ open: true, message, severity })
  }

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }))
  }

  // 加载品牌详情
  const fetchBrandDetail = () => {
    if (!brandId) return
    setLoading(true)
    getBrandDetail(brandId)
      .then((data) => {
        setBrand(data)
        // 输入框回填当前已配置的老板密码（未配置则为空）
        setBossPassword(data.boss_password ?? '')
        // 回填 slug 与主题色（未配置时 slug 为空、颜色用默认值）
        setSlug(data.slug ?? '')
        setPrimaryColor(data.config?.primary_color || DEFAULT_PRIMARY_COLOR)
        setSecondaryColor(data.config?.secondary_color || DEFAULT_SECONDARY_COLOR)
        setError(null)
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : '加载品牌详情失败')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchBrandDetail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId])

  // 编辑品牌信息
  const handleEdit = () => {
    setFormOpen(true)
  }

  // 提交品牌信息更新
  const handleFormSubmit = async (data: BrandUpdateParams) => {
    if (!brandId) return
    setSubmitting(true)
    try {
      await updateBrand(brandId, data)
      showToast('品牌信息已更新', 'success')
      setFormOpen(false)
      fetchBrandDetail()
    } catch (e) {
      showToast(e instanceof Error ? e.message : '更新失败', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  // 提交配置更新
  const handleConfigSubmit = async (config: BrandConfigParams) => {
    if (!brandId) return
    setSubmitting(true)
    try {
      await updateBrandConfig(brandId, config)
      showToast('品牌配置已保存', 'success')
      setConfigOpen(false)
      fetchBrandDetail()
    } catch (e) {
      showToast(e instanceof Error ? e.message : '保存失败', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  // 查看通用入口二维码（老板 / 员工共用同一个码）
  // 一次请求拿回海报版 + 登录版两种形态，弹窗内切换不再发请求
  const handleViewQrcode = async () => {
    if (!brandId) return
    try {
      const data = await getBrandQrcode(brandId, 'poster')
      setQrcodeData(data)
      setQrVariant('poster')
      setQrcodeOpen(true)
    } catch (e) {
      showToast(e instanceof Error ? e.message : '获取二维码失败', 'error')
    }
  }

  // 当前选中形态对应的二维码图与落地链接
  // 后端未升级（缺 poster_* 字段）时自动回落兼容字段，弹窗不会白屏
  const currentQrcodeImage: string =
    (qrVariant === 'poster' ? qrcodeData?.poster_qrcode_url : qrcodeData?.login_qrcode_url) ||
    qrcodeData?.qrcode_url ||
    ''

  const currentQrcodeLink: string =
    (qrVariant === 'poster' ? qrcodeData?.poster_url : qrcodeData?.login_url) ||
    qrcodeData?.h5_url ||
    ''

  /**
   * 品牌海报页 URL（供预览 / 卡片点击用）：/qr/<slug>。
   * 海报页是 H5（员工端）应用，与后台（admin）部署在不同端口/路径：
   * - 后台在 8080 或 /admin 子路径；
   * - H5（含海报页 /qr/）在标准 80/443 根路径。
   * 因此不能用 window.location.origin（那是后台地址，会落到后台 → 登录页），
   * 需去掉后台专用端口 8080，回落到 H5 所在的标准端口。
   */
  const h5Origin: string = (() => {
    const loc: Location = window.location
    if (loc.port === '8080') return `${loc.protocol}//${loc.hostname}`
    return loc.origin
  })()
  const posterPageUrl: string = brand?.slug
    ? `${h5Origin}/qr/${encodeURIComponent(brand.slug)}`
    : ''

  /** 海报版是否可用（后端已升级才会返回 poster_url） */
  const posterSupported: boolean = Boolean(qrcodeData?.poster_url)

  // 下载当前二维码 PNG：后端返回的是 Data URL，直接挂 <a download> 即可，无需再请求
  const handleDownloadQrcode = () => {
    if (!currentQrcodeImage) {
      showToast('二维码尚未生成，请稍后重试', 'error')
      return
    }

    const brandLabel: string = brand?.slug || brand?.name || 'brand'
    const variantLabel: string = qrVariant === 'poster' ? '海报版' : '登录版'

    const link = document.createElement('a')
    link.href = currentQrcodeImage
    link.download = `${brandLabel}-入口二维码-${variantLabel}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    showToast('二维码已下载', 'success')
  }

  // 复制当前落地链接（方便直接发微信而不用截图）
  const handleCopyQrcodeLink = async () => {
    if (!currentQrcodeLink) return
    const ok = await copyToClipboard(currentQrcodeLink)
    showToast(ok ? '链接已复制' : '复制失败，请手动复制', ok ? 'success' : 'error')
  }

  // 复制品牌口令
  const handleCopyCode = async () => {
    if (!brand?.access_code) return
    const ok = await copyToClipboard(brand.access_code)
    showToast(ok ? '口令已复制' : '复制失败，请手动复制', ok ? 'success' : 'error')
  }

  // 保存老板专属密码（复用更新品牌接口；空字符串表示清空 = 停用老板入口）
  const handleSaveBossPassword = async () => {
    if (!brandId) return

    const trimmed = bossPassword.trim()
    // 与后端 validate_boss_password 保持同一规则，避免白跑一趟 400
    if (trimmed && (trimmed.length < 4 || trimmed.length > 32)) {
      showToast('老板密码需为 4-32 位', 'error')
      return
    }
    if (/\s/.test(trimmed)) {
      showToast('老板密码不能包含空格', 'error')
      return
    }

    setSavingBossPassword(true)
    try {
      const updated = await updateBrand(brandId, { boss_password: trimmed })
      // 后端返回 BrandDetail（含明文），直接就地回显，避免整页重载
      setBrand((prev) => (prev ? { ...prev, boss_password: updated.boss_password ?? null } : prev))
      setBossPassword(updated.boss_password ?? '')
      showToast(trimmed ? '老板密码已保存' : '老板密码已清空，老板入口已停用', 'success')
    } catch (e) {
      showToast(e instanceof Error ? e.message : '保存失败', 'error')
    } finally {
      setSavingBossPassword(false)
    }
  }

  // 保存客户英文标识（slug）：复用更新品牌接口；空字符串表示清空（入口回落 UUID）
  const handleSaveSlug = async () => {
    if (!brandId) return

    // 归一化为小写：后端也会做同样处理，这里先做让用户所见即所得
    const normalized = slug.trim().toLowerCase()
    if (normalized && !isValidSlug(normalized)) {
      showToast('英文标识需为 3-64 位小写字母、数字或中划线，且不以中划线开头/结尾', 'error')
      return
    }

    setSavingSlug(true)
    try {
      const updated = await updateBrand(brandId, { slug: normalized })
      setBrand((prev) => (prev ? { ...prev, slug: updated.slug ?? null } : prev))
      setSlug(updated.slug ?? '')
      showToast(
        normalized ? '英文标识已保存，二维码链接将使用该标识' : '英文标识已清空，入口回落品牌 ID',
        'success',
      )
    } catch (e) {
      // slug 被其他品牌占用时后端返回 400 + 明确文案，直接透传
      showToast(e instanceof Error ? e.message : '保存失败', 'error')
    } finally {
      setSavingSlug(false)
    }
  }

  // 保存 H5 主题色（走配置更新接口；后端只写入非空字段，不会清空其它调性配置）
  const handleSaveColors = async () => {
    if (!brandId) return

    setSavingColors(true)
    try {
      const config = brand?.config
      await updateBrandConfig(brandId, {
        // 带上已有配置原样回传，保证「只改颜色」不影响其它字段
        tone_style: config?.tone_style ?? '',
        forbidden_words: config?.forbidden_words ?? '',
        required_tags: config?.required_tags ?? '',
        image_style: config?.image_style ?? '',
        color_tone: config?.color_tone ?? '',
        category_keywords: config?.category_keywords ?? '',
        primary_color: primaryColor,
        secondary_color: secondaryColor,
      })
      showToast('主题色已保存，客户 H5 将使用新配色', 'success')
      fetchBrandDetail()
    } catch (e) {
      showToast(e instanceof Error ? e.message : '保存失败', 'error')
    } finally {
      setSavingColors(false)
    }
  }

  // 复制老板密码
  const handleCopyBossPassword = async () => {
    if (!brand?.boss_password) return
    const ok = await copyToClipboard(brand.boss_password)
    showToast(ok ? '老板密码已复制' : '复制失败，请手动复制', ok ? 'success' : 'error')
  }

  // ===== 门店管理（P0-1 门店独立化）=====

  // 拉取门店列表（含禁用，admin 全量）
  const fetchStores = () => {
    if (!brandId) return
    setStoresLoading(true)
    listStores(brandId)
      .then((data) => setStores(data || []))
      .catch((e) => showToast(e instanceof Error ? e.message : '加载门店列表失败', 'error'))
      .finally(() => setStoresLoading(false))
  }

  useEffect(() => {
    fetchStores()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId])

  // 把招牌菜输入（换行 / 顿号 / 逗号分隔）拆成数组
  const parseDishes = (raw: string): string[] => {
    if (!raw) return []
    return raw
      .split(/[\n、,，;；/／]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
  }

  // 打开新增门店弹窗
  const handleOpenNewStore = () => {
    setEditingStore(null)
    setStoreForm({
      name: '',
      city: '',
      address: '',
      signature_dishes: '',
      sort_order: (stores.length || 0) + 1, // 默认排在末尾
      is_active: true,
    })
    setStoreDialogOpen(true)
  }

  // 打开编辑门店弹窗（回填）
  const handleOpenEditStore = (store: Store) => {
    setEditingStore(store)
    setStoreForm({
      name: store.name || '',
      city: store.city || '',
      address: store.address || '',
      signature_dishes: (store.signature_dishes || []).join('、'),
      sort_order: store.sort_order ?? 0,
      is_active: store.is_active ?? true,
    })
    setStoreDialogOpen(true)
  }

  // 提交门店新增 / 更新
  const handleStoreSubmit = async () => {
    if (!brandId) return
    if (!storeForm.name.trim()) {
      showToast('请填写门店名称', 'error')
      return
    }
    setSavingStore(true)
    try {
      const payload: StoreCreateParams = {
        name: storeForm.name.trim(),
        city: storeForm.city.trim(),
        address: storeForm.address.trim(),
        signature_dishes: parseDishes(storeForm.signature_dishes),
        sort_order: Number(storeForm.sort_order) || 0,
        is_active: storeForm.is_active,
      }
      if (editingStore) {
        const upd: StoreUpdateParams = payload
        await updateStore(brandId, editingStore.id, upd)
        showToast('门店已更新', 'success')
      } else {
        await createStore(brandId, payload)
        showToast('门店已创建', 'success')
      }
      setStoreDialogOpen(false)
      // 重新拉列表拿到完整数据（含新建的 id / 时间戳）
      fetchStores()
    } catch (e) {
      showToast(e instanceof Error ? e.message : '保存失败', 'error')
    } finally {
      setSavingStore(false)
    }
  }

  // 确认删除门店
  const handleConfirmDeleteStore = async () => {
    if (!brandId || !deleteStoreTarget) return
    setDeletingStore(true)
    try {
      await deleteStore(brandId, deleteStoreTarget.id)
      showToast('门店已删除', 'success')
      setDeleteStoreTarget(null)
      fetchStores()
    } catch (e) {
      showToast(e instanceof Error ? e.message : '删除失败', 'error')
    } finally {
      setDeletingStore(false)
    }
  }

  // 重置品牌口令
  const handleResetCode = async () => {
    if (!brandId) return
    setResetting(true)
    try {
      const res = await resetAccessCode(brandId)
      setBrand((prev) => (prev ? { ...prev, access_code: res.access_code } : prev))
      showToast('口令已重置', 'success')
    } catch (e) {
      showToast(e instanceof Error ? e.message : '重置失败', 'error')
    } finally {
      setResetting(false)
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#FF8C00' }} />
        </Box>
      </AdminLayout>
    )
  }

  if (error || !brand) {
    return (
      <AdminLayout>
        <Card sx={{ borderRadius: '12px' }}>
          <CardContent sx={{ p: 3, textAlign: 'center', color: '#F44336' }}>
            {error || '品牌不存在'}
          </CardContent>
        </Card>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      {/* 返回 + 标题 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/brands')}
          sx={{ color: '#666', textTransform: 'none' }}
        >
          返回列表
        </Button>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', flex: 1 }}>
          {brand.name}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={handleEdit}
          sx={{ borderColor: '#ddd', color: '#666', textTransform: 'none', mr: 1 }}
        >
          编辑信息
        </Button>
        <Button
          variant="outlined"
          startIcon={<TuneIcon />}
          onClick={() => setConfigOpen(true)}
          sx={{ borderColor: '#FF8C00', color: '#FF8C00', textTransform: 'none' }}
        >
          配置调性
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* 左侧：基本信息 */}
        <Grid item xs={7}>
          <Card sx={{ borderRadius: '12px', mb: 3 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                基本信息
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>品牌名称</Typography>
                  <Typography variant="body1" sx={{ color: '#333', fontWeight: 500 }}>{brand.name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>子品牌</Typography>
                  <Typography variant="body1" sx={{ color: '#333' }}>{brand.sub_brand || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>品类</Typography>
                  <Typography variant="body1" sx={{ color: '#333' }}>{brand.category || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>状态</Typography>
                  <Chip
                    label={STATUS_LABELS[brand.status] || brand.status}
                    size="small"
                    sx={{
                      backgroundColor: `${STATUS_COLORS[brand.status]}15`,
                      color: STATUS_COLORS[brand.status],
                      fontSize: '12px',
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>Slogan</Typography>
                  <Typography variant="body1" sx={{ color: '#333' }}>{brand.slogan || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>额度池</Typography>
                  <Typography variant="body1" sx={{ color: '#333' }}>
                    {brand.quota_total ?? 0} 点（已用 {brand.quota_used ?? 0} 点）
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>试用开始</Typography>
                  <Typography variant="body1" sx={{ color: '#333' }}>{formatDateOnly(brand.trial_start)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>试用截止</Typography>
                  <Typography variant="body1" sx={{ color: '#333' }}>{formatDateOnly(brand.trial_end)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>计费模式</Typography>
                  <Typography variant="body1" sx={{ color: '#333' }}>
                    {brand.billing_type === 'pay_per_use' ? '按点计费' : (brand.billing_type || '按点计费')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>单点金额</Typography>
                  <Typography variant="body1" sx={{ color: '#333' }}>
                    ¥{((brand.unit_price ?? 0) / 100).toFixed(2)} / 条
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* 员工登录口令（完整明文，供品牌方复制分发） */}
          <Card sx={{ borderRadius: '12px', mb: 3 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>
                  员工登录口令
                </Typography>
                <Box>
                  <Button
                    size="small"
                    onClick={() => setShowCode((s) => !s)}
                    sx={{ color: '#999', textTransform: 'none', minWidth: 0, mr: 1 }}
                  >
                    {showCode ? '隐藏' : '显示'}
                  </Button>
                  <Button
                    size="small"
                    onClick={handleCopyCode}
                    sx={{ color: '#FF8C00', textTransform: 'none', minWidth: 0 }}
                  >
                    复制
                  </Button>
                </Box>
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '18px',
                  letterSpacing: '1px',
                  fontWeight: 600,
                  color: '#333',
                  wordBreak: 'break-all',
                }}
              >
                {brand.access_code
                  ? showCode
                    ? brand.access_code
                    : maskAccessCode(brand.access_code)
                  : '-'}
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleResetCode}
                sx={{
                  mt: 2,
                  borderColor: '#FF8C00',
                  color: '#FF8C00',
                  textTransform: 'none',
                }}
              >
                重置口令
              </Button>
            </CardContent>
          </Card>

          {/* H5 主题色（方案 A：每品牌主色 + 辅色，客户端动态换肤） */}
          <Card sx={{ borderRadius: '12px', mb: 3 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 0.5 }}>
                H5 主题色
              </Typography>
              <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 2 }}>
                客户端顶栏、按钮、卡片渐变会使用这两个颜色；不配置则使用系统默认配色。
              </Typography>

              <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#999', fontSize: '12px', mb: 0.5 }}>
                    主色（primary）
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      component="input"
                      type="color"
                      value={primaryColor}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setPrimaryColor(e.target.value)
                      }
                      sx={{
                        width: 48,
                        height: 36,
                        padding: 0,
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        backgroundColor: 'transparent',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', color: '#333', textTransform: 'uppercase' }}
                    >
                      {primaryColor}
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="body2" sx={{ color: '#999', fontSize: '12px', mb: 0.5 }}>
                    辅色（secondary）
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      component="input"
                      type="color"
                      value={secondaryColor}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setSecondaryColor(e.target.value)
                      }
                      sx={{
                        width: 48,
                        height: 36,
                        padding: 0,
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        backgroundColor: 'transparent',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', color: '#333', textTransform: 'uppercase' }}
                    >
                      {secondaryColor}
                    </Typography>
                  </Box>
                </Box>

                {/* 实时预览：主色渐变条 + 主/辅色按钮示意 */}
                <Box sx={{ flex: 1, minWidth: 180 }}>
                  <Typography variant="body2" sx={{ color: '#999', fontSize: '12px', mb: 0.5 }}>
                    效果预览
                  </Typography>
                  <Box
                    sx={{
                      height: 36,
                      borderRadius: '8px',
                      background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                    }}
                  />
                </Box>
              </Box>

              <Button
                variant="contained"
                onClick={handleSaveColors}
                disabled={savingColors}
                sx={{
                  mt: 2,
                  backgroundColor: '#FF8C00',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: '#E67E00' },
                }}
              >
                {savingColors ? '保存中' : '保存主题色'}
              </Button>
            </CardContent>
          </Card>

          {/* 品牌配置 */}
          <Card sx={{ borderRadius: '12px' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>
                  AI 生成配置
                </Typography>
                <Button
                  size="small"
                  startIcon={<TuneIcon />}
                  onClick={() => setConfigOpen(true)}
                  sx={{ color: '#FF8C00', textTransform: 'none' }}
                >
                  编辑配置
                </Button>
              </Box>
              {brand.config ? (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>调性风格</Typography>
                    <Typography variant="body2" sx={{ color: '#333' }}>{brand.config.tone_style || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>图片风格</Typography>
                    <Typography variant="body2" sx={{ color: '#333' }}>{brand.config.image_style || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>色调偏好</Typography>
                    <Typography variant="body2" sx={{ color: '#333' }}>{brand.config.color_tone || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>品类关键词</Typography>
                    <Typography variant="body2" sx={{ color: '#333' }}>{brand.config.category_keywords || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>禁用词</Typography>
                    <Typography variant="body2" sx={{ color: '#333' }}>{brand.config.forbidden_words || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>必带标签</Typography>
                    <Typography variant="body2" sx={{ color: '#333' }}>{brand.config.required_tags || '-'}</Typography>
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" sx={{ color: '#999', textAlign: 'center', py: 2 }}>
                  暂未配置，点击"编辑配置"设置 AI 生成调性
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* 门店管理（P0-1 门店独立化）：每个门店可发不同内容 */}
          <Card sx={{ borderRadius: '12px' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>
                  门店管理
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleOpenNewStore}
                  sx={{ color: '#FF8C00', textTransform: 'none' }}
                >
                  新增门店
                </Button>
              </Box>
              <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 1.5 }}>
                不同门店可发不同内容（城市 / 地址 / 招牌菜独立），开头去重细化到「门店 + 体裁」。
              </Typography>
              {storesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} sx={{ color: '#FF8C00' }} />
                </Box>
              ) : stores.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#999', textAlign: 'center', py: 2 }}>
                  暂无门店，点击「新增门店」添加
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {stores.map((s) => (
                    <Box
                      key={s.id}
                      sx={{
                        p: 1.5,
                        borderRadius: '8px',
                        border: '1px solid #f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        opacity: s.is_active ? 1 : 0.5,
                      }}
                    >
                      <StorefrontIcon fontSize="small" sx={{ color: '#FF8C00' }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                          {s.name}
                          {!s.is_active && (
                            <Chip
                              label="已禁用"
                              size="small"
                              sx={{ ml: 1, height: 18, fontSize: '10px', backgroundColor: '#eee', color: '#999' }}
                            />
                          )}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                          {[s.city, s.address].filter(Boolean).join(' · ') || '未填写城市 / 地址'}
                        </Typography>
                        {s.signature_dishes && s.signature_dishes.length > 0 && (
                          <Typography variant="caption" sx={{ color: '#bbb', display: 'block' }}>
                            招牌菜：{s.signature_dishes.join('、')}
                          </Typography>
                        )}
                      </Box>
                      <IconButton size="small" onClick={() => handleOpenEditStore(s)} sx={{ color: '#666' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteStoreTarget(s)}
                        sx={{ color: '#F44336' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 右侧：口令 / 二维码 / 时间 */}
        <Grid item xs={5}>
          {/* 品牌通用口令 */}
          <Card sx={{ borderRadius: '12px', mb: 3 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>
                  品牌通用口令
                </Typography>
                <Box>
                  <Tooltip title="复制口令">
                    <IconButton size="small" onClick={handleCopyCode} sx={{ color: '#FF8C00' }}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="重置口令">
                    <IconButton
                      size="small"
                      onClick={handleResetCode}
                      disabled={resetting}
                      sx={{ color: '#666' }}
                    >
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '8px',
                  backgroundColor: '#f7f7f7',
                  fontFamily: 'monospace',
                  fontSize: '16px',
                  letterSpacing: '1px',
                  color: '#333',
                  wordBreak: 'break-all',
                }}
              >
                {brand.access_code || '-'}
              </Box>
              <Typography variant="caption" sx={{ color: '#999', mt: 1, display: 'block' }}>
                员工扫通用入口码后输入姓名与此口令即可登录（普通权限：发笔记）
              </Typography>
            </CardContent>
          </Card>

          {/* 老板专属密码（每个品牌单独配置，最高权限） */}
          <Card sx={{ borderRadius: '12px', mb: 3 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>
                  老板密码
                </Typography>
                <Tooltip title="复制老板密码">
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleCopyBossPassword}
                      disabled={!brand.boss_password}
                      sx={{ color: '#2196F3' }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  size="small"
                  value={bossPassword}
                  onChange={(e) => setBossPassword(e.target.value)}
                  placeholder="4-32 位，不含空格；留空表示停用老板入口"
                  inputProps={{ maxLength: 32 }}
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }}
                />
                <Button
                  variant="contained"
                  onClick={handleSaveBossPassword}
                  disabled={savingBossPassword}
                  sx={{
                    backgroundColor: '#FF8C00',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': { backgroundColor: '#E67E00' },
                  }}
                >
                  {savingBossPassword ? '保存中' : '保存'}
                </Button>
              </Box>
              <Typography variant="caption" sx={{ color: '#999', mt: 1, display: 'block' }}>
                老板扫**同一个**通用入口码，输入姓名与此密码登录即为最高权限
                （可发笔记 + 看老板看板），无需再输一次密码。
              </Typography>
              {!brand.boss_password && (
                <Typography variant="caption" sx={{ color: '#FF9800', mt: 0.5, display: 'block' }}>
                  当前未配置，该品牌暂无老板入口。
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* 客户英文标识（slug）：入口参数，替代 UUID */}
          <Card sx={{ borderRadius: '12px', mb: 3 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                客户英文标识
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  size="small"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="如 haohongtianxia（3-64 位小写字母/数字/中划线）"
                  inputProps={{ maxLength: 64 }}
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }}
                />
                <Button
                  variant="contained"
                  onClick={handleSaveSlug}
                  disabled={savingSlug}
                  sx={{
                    backgroundColor: '#FF8C00',
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': { backgroundColor: '#E67E00' },
                  }}
                >
                  {savingSlug ? '保存中' : '保存'}
                </Button>
              </Box>
              <Typography variant="caption" sx={{ color: '#999', mt: 1, display: 'block' }}>
                入口链接会变成 <code>?brand={slug.trim().toLowerCase() || '<品牌ID>'}</code>，
                比 UUID 更短更好念。全局唯一，留空则继续使用品牌 ID（老链接始终有效）。
              </Typography>
              {!brand.slug && (
                <Typography variant="caption" sx={{ color: '#FF9800', mt: 0.5, display: 'block' }}>
                  当前未配置，入口二维码仍使用品牌 ID。
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* 品牌扫码海报入口（点击主体打开 /qr/<slug> 海报页） */}
          <Card sx={{ borderRadius: '12px' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                品牌扫码海报
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  borderRadius: '8px',
                  border: '1px solid #f0f0f0',
                  '&:hover': { backgroundColor: '#fafafa' },
                }}
              >
                <Box
                  onClick={() => posterPageUrl && window.open(posterPageUrl, '_blank', 'noopener,noreferrer')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flex: 1,
                    cursor: posterPageUrl ? 'pointer' : 'default',
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '8px',
                      backgroundColor: '#FFF0E0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#FF8C00',
                    }}
                  >
                    <QrCodeIcon />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                      查看 / 打开品牌海报页
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999' }}>
                      点击打开员工扫码海报，可截图 / 保存转发员工群
                    </Typography>
                  </Box>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  disabled={!posterPageUrl}
                  onClick={handleViewQrcode}
                  sx={{
                    flexShrink: 0,
                    textTransform: 'none',
                    borderColor: '#eee',
                    color: '#666',
                    '&:hover': { backgroundColor: '#f5f5f5', borderColor: '#ddd' },
                  }}
                >
                  下载二维码
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* 时间信息 */}
          <Card sx={{ borderRadius: '12px', mt: 3 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                时间信息
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: '#999' }}>创建时间</Typography>
                  <Typography variant="body2" sx={{ color: '#333' }}>{formatDate(brand.created_at)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: '#999' }}>更新时间</Typography>
                  <Typography variant="body2" sx={{ color: '#333' }}>{formatDate(brand.updated_at)}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 品牌编辑弹窗 */}
      <BrandFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit as any}
        brand={brand}
        submitting={submitting}
      />

      {/* 品牌配置弹窗 */}
      <BrandConfigDialog
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        onSubmit={handleConfigSubmit}
        config={brand.config || null}
        brandName={brand.name}
        submitting={submitting}
      />

      {/* 二维码弹窗 */}
      <Dialog open={qrcodeOpen} onClose={() => setQrcodeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, textAlign: 'center' }}>
          通用入口二维码
          {qrcodeData?.brand_name ? ` - ${qrcodeData.brand_name}` : ''}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 1 }}>
            {/* 落地形态切换：海报版（默认）/ 登录版 */}
            <ToggleButtonGroup
              exclusive
              size="small"
              value={qrVariant}
              onChange={(_, next: QrVariant | null) => {
                if (next) setQrVariant(next)
              }}
              sx={{
                mb: 2,
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  px: 2,
                  fontSize: '13px',
                  borderColor: '#eee',
                  color: '#666',
                },
                '& .Mui-selected': {
                  backgroundColor: '#FFF0E0 !important',
                  color: '#FF8C00 !important',
                  borderColor: '#FFD1A0 !important',
                },
              }}
            >
              <ToggleButton value="poster" disabled={!posterSupported}>
                海报版（推荐）
              </ToggleButton>
              <ToggleButton value="login">登录版</ToggleButton>
            </ToggleButtonGroup>

            {currentQrcodeImage && (
              <Box
                component="img"
                src={currentQrcodeImage}
                alt={qrVariant === 'poster' ? '海报版入口二维码' : '登录版入口二维码'}
                sx={{
                  width: 240,
                  height: 240,
                  margin: '0 auto',
                  display: 'block',
                  border: '1px solid #f0f0f0',
                  borderRadius: '8px',
                }}
              />
            )}

            <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 1.5 }}>
              {qrVariant === 'poster'
                ? '扫码进入品牌海报页，页面内二维码可长按存图转发员工群'
                : '扫码直达登录表单，适合已熟悉流程的老员工'}
            </Typography>

            {/* 操作区：下载 PNG / 预览海报页 / 复制链接 */}
            <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: 'center' }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadQrcode}
                sx={{
                  backgroundColor: '#FF8C00',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: '#E67E00' },
                }}
              >
                下载二维码
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                disabled={!posterPageUrl}
                onClick={() => window.open(posterPageUrl, '_blank', 'noopener,noreferrer')}
                sx={{ borderColor: '#ddd', color: '#666', textTransform: 'none' }}
              >
                预览海报页
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                disabled={!currentQrcodeLink}
                onClick={handleCopyQrcodeLink}
                sx={{ borderColor: '#ddd', color: '#666', textTransform: 'none' }}
              >
                复制链接
              </Button>
            </Stack>

            <Divider sx={{ my: 2 }} />

            {currentQrcodeLink && (
              <Typography
                variant="body2"
                sx={{ color: '#999', wordBreak: 'break-all', fontSize: '12px' }}
              >
                落地链接：{currentQrcodeLink}
              </Typography>
            )}

            <Typography variant="caption" sx={{ color: '#bbb', display: 'block', mt: 1 }}>
              老板与员工共用此码：员工输通用口令，老板输老板密码
            </Typography>

            {/* 打开方式引导：微信 / App 内置 WebView 的 localStorage 不持久，
                关闭窗口即丢登录态，导致「每次扫码都要重新登录」。 */}
            <Alert
              severity="info"
              icon={false}
              sx={{
                mt: 2,
                borderRadius: '8px',
                fontSize: '12px',
                textAlign: 'left',
                lineHeight: 1.8,
              }}
            >
              请用手机<strong>系统浏览器</strong>扫描；若用微信扫，点右上角 ··· →
              在浏览器打开。登录后可把本页「添加到主屏幕」，下次免登录直接进。
            </Alert>
          </Box>
        </DialogContent>
      </Dialog>

      {/* 口令重置结果弹窗 */}
      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, textAlign: 'center' }}>
          口令已重置
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>
              新的品牌通用口令如下，请复制分发给员工：
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'monospace',
                fontSize: '20px',
                letterSpacing: '1px',
                fontWeight: 600,
                color: '#333',
                wordBreak: 'break-all',
                mb: 2,
              }}
            >
              {newCode}
            </Typography>
            <Alert severity="info" variant="filled" sx={{ fontSize: '13px', borderRadius: '8px' }}>
              轮换口令后，用旧口令新登录的会话将立即失效；已勾选「记住我」的存量会话最长 30
              天内仍有效。为安全起见，建议通知员工重新登录以清除旧会话。
            </Alert>
          </Box>
        </DialogContent>
      </Dialog>

      {/* 门店新增 / 编辑弹窗（P0-1 门店独立化） */}
      <Dialog open={storeDialogOpen} onClose={() => setStoreDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editingStore ? `编辑门店 - ${editingStore.name}` : '新增门店'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              fullWidth
              required
              size="small"
              label="门店名称"
              value={storeForm.name}
              onChange={(e) => setStoreForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="如 大八滨江店"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="城市"
                value={storeForm.city}
                onChange={(e) => setStoreForm((prev) => ({ ...prev, city: e.target.value }))}
                placeholder="如 上海"
              />
              <TextField
                fullWidth
                size="small"
                label="排序（越小越靠前）"
                type="number"
                value={storeForm.sort_order}
                onChange={(e) =>
                  setStoreForm((prev) => ({ ...prev, sort_order: Number(e.target.value) || 0 }))
                }
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              label="门店地址"
              value={storeForm.address}
              onChange={(e) => setStoreForm((prev) => ({ ...prev, address: e.target.value }))}
              placeholder="如 浦东新区滨江大道 X 号"
            />
            <TextField
              fullWidth
              size="small"
              label="招牌菜（选填）"
              value={storeForm.signature_dishes}
              onChange={(e) => setStoreForm((prev) => ({ ...prev, signature_dishes: e.target.value }))}
              placeholder="每行一个，或用顿号 / 逗号分隔，如 招牌龙虾、蒜蓉生蚝"
              multiline
              minRows={2}
              helperText="留空则回落品牌级招牌菜"
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: '#666' }}>启用</Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={storeForm.is_active ? 'on' : 'off'}
                onChange={(_, next: string | null) => {
                  if (next) setStoreForm((prev) => ({ ...prev, is_active: next === 'on' }))
                }}
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 2, fontSize: '13px' } }}
              >
                <ToggleButton value="on">启用</ToggleButton>
                <ToggleButton value="off">禁用</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" sx={{ color: '#999' }}>
                禁用后不在 H5 选店列表出现
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 3, pb: 2 }}>
          <Button
            onClick={() => setStoreDialogOpen(false)}
            sx={{ textTransform: 'none', color: '#666' }}
          >
            取消
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleStoreSubmit}
            disabled={savingStore}
            sx={{
              backgroundColor: '#FF8C00',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#E67E00' },
            }}
          >
            {savingStore ? '保存中' : '保存'}
          </Button>
        </Box>
      </Dialog>

      {/* 门店删除确认弹窗 */}
      <Dialog open={Boolean(deleteStoreTarget)} onClose={() => setDeleteStoreTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>确认删除门店</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#666' }}>
            确定要删除门店「<strong>{deleteStoreTarget?.name}</strong>」吗？删除后该门店不再出现在 H5
            选店列表（历史笔记仍保留门店维度，不会丢失）。
          </Typography>
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteStoreTarget(null)}
            sx={{ textTransform: 'none', color: '#666' }}
          >
            取消
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleConfirmDeleteStore}
            disabled={deletingStore}
            sx={{ textTransform: 'none' }}
          >
            {deletingStore ? '删除中' : '删除'}
          </Button>
        </Box>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
    </AdminLayout>
  )
}

export default BrandDetailPage
